import React, { useState, useEffect, useRef } from "react";
import {
  TextField,
  Button,
  Select,
  Container,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Box,
  Typography,
  Paper,
  AppBar,
  Toolbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Modal,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import LoadingBar from "react-top-loading-bar";
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const AddBranch = () => {
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const navigate = useNavigate();
  const [loadingBarProgress, setLoadingBarProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({
    OrganisationId: "",
    BranchName: "",
    BranchAddress: "",
    CityId: "",
    StateId: "",
    CountryId: "",
    ZipCode: "",
    MobileNo: "",
    PhoneNo: "",
    TinNo: "",
    AffiliationNo: "",
    Fax: "",
    EmailID: "",
    Website: "",
    OrgBranchLogo: null,
    OrgBranchImgUrl: "",
  });
  const [branchLogoPreview, setBranchLogoPreview] = useState("");
  const [organizations, setOrganizations] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  // Fetch organizations
  const fetchOrganizations = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/Organization/GetOrganization`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({}),
      });
      if (response.ok) {
        const data = await response.json();
        setOrganizations(data || []);
      }
    } catch (err) {
      setError("Failed to fetch organizations");
    }
  };

  // Fetch countries
  const fetchCountries = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/Country/GetCountry`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({}),
      });
      if (response.ok) {
        const data = await response.json();
        setCountries(data || []);
      }
    } catch (err) {
      setError("Failed to fetch countries");
    }
  };

  // Fetch states
  const fetchStates = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/State/GetState`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({}),
      });
      if (response.ok) {
        const data = await response.json();
        setStates(data || []);
      }
    } catch (err) {
      setError("Failed to fetch states");
    }
  };

  // Fetch cities
  const fetchCities = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/City/GetCity`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({}),
      });
      if (response.ok) {
        const data = await response.json();
        setCities(data || []);
      }
    } catch (err) {
      setError("Failed to fetch cities");
    }
  };

  useEffect(() => {
    setIsLoading(true);
    Promise.all([
      fetchOrganizations(),
      fetchCountries(),
      fetchStates(),
      fetchCities(),
    ]).then(() => {
      setLoadingBarProgress(100);
      setIsLoading(false);
    });
  }, []);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle file input for logo
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!["image/jpeg", "image/jpg"].includes(file.type)) {
        setSnackbarMessage('Please select a JPG or JPEG file.');
        setOpenSnackbar(true);
        setSnackbarSeverity("warning");
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        setSnackbarMessage('File size must be less than 2MB.');
        setOpenSnackbar(true);
        setSnackbarSeverity("warning");
        return;
      }
      setFormData((prev) => ({ ...prev, OrgBranchLogo: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setBranchLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
      setOpenDialog(false);
    }
  };

  // Handle opening the file picker
  const handleChooseFile = () => {
    fileInputRef.current.click();
  };

  // Handle opening the instruction dialog
  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  // Handle dialog close
  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");

      if (!token) {
        throw new Error("Authentication token is missing");
      }

      const formDataToSend = new FormData();

      // Helper function to handle empty values
      const formatValue = (value, isNumber = false) => {
        if (value === undefined || value === null || value === "") {
          return isNumber ? "0" : "empty";
        }
        return value;
      };

      // Append all required fields
      formDataToSend.append("OrganisationId", formatValue(formData.OrganisationId, true));
      formDataToSend.append("BranchName", formatValue(formData.BranchName));
      formDataToSend.append("BranchAddress", formatValue(formData.BranchAddress));
      formDataToSend.append("CityId", formatValue(formData.CityId, true));
      formDataToSend.append("StateId", formatValue(formData.StateId, true));
      formDataToSend.append("CountryId", formatValue(formData.CountryId, true));
      formDataToSend.append("ZipCode", formatValue(formData.ZipCode));
      formDataToSend.append("MobileNo", formatValue(formData.MobileNo));
      formDataToSend.append("PhoneNo", formatValue(formData.PhoneNo));
      formDataToSend.append("TinNo", formatValue(formData.TinNo));
      formDataToSend.append("AffiliationNo", formatValue(formData.AffiliationNo));
      formDataToSend.append("Fax", formatValue(formData.Fax));
      formDataToSend.append("EmailID", formatValue(formData.EmailID));
      formDataToSend.append("Website", formatValue(formData.Website));
      formDataToSend.append("OrgBranchImgUrl", formatValue(formData.OrgBranchImgUrl));

      // Handle logo submission
      if (formData.OrgBranchLogo) {
        formDataToSend.append("OrgBranchLogo", formData.OrgBranchLogo);
      } else {
        formDataToSend.append("OrgBranchLogo", new Blob(), "empty.jpg");
      }

      setLoadingBarProgress(30);

      const response = await fetch(`${apiUrl}/Branch`, {
        method: "POST",
        headers: {
          Authorization: token,
        },
        body: formDataToSend,
      });

      if (response.ok) {
        setShowSuccessModal(true);
      } else {
        const errorMessage = await response.text();
        setError(`Failed to add branch: ${errorMessage} (Status: ${response.status})`);
        console.error("API Error:", errorMessage);
      }
    } catch (err) {
      console.error("Request failed:", err);
      setError("An error occurred while adding branch: " + err.message);
    } finally {
      setLoadingBarProgress(100);
    }
  };

  return (
    <Container>
      <AppBar position="static" style={{ backgroundColor: "#0B1F3D" }}>
        <Toolbar>
          <Typography variant="h4" component="div" style={{ fontWeight: 'bold' }}>
            Add Branch
          </Typography>
        </Toolbar>
      </AppBar>
      <LoadingBar
        progress={loadingBarProgress}
        color="rgb(152, 106, 182)"
        height={5}
      />
      <Paper elevation={3} style={{ padding: "24px", marginTop: 6 }}>
        {isLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="50vh"
          >
            <CircularProgress size={60} />
          </Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <form onSubmit={handleSubmit}>
            <Box display="flex" flexDirection="column" gap={3}>
              <FormControl fullWidth>
                <InputLabel>Organization</InputLabel>
                <Select
                  name="OrganisationId"
                  value={formData.OrganisationId}
                  onChange={handleChange}
                  required
                >
                  <MenuItem value="" disabled>Select Organization</MenuItem>
                  {organizations.map((org) => (
                    <MenuItem key={org.schoolId} value={org.schoolId}>
                      {org.schoolName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Branch Name"
                name="BranchName"
                value={formData.BranchName}
                onChange={handleChange}
                fullWidth
                required
                variant="outlined"
                sx={{ "& .MuiInputLabel-root": { top: "-6px" } }}
              />
              <TextField
                label="Branch Address"
                name="BranchAddress"
                value={formData.BranchAddress}
                onChange={handleChange}
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                sx={{ "& .MuiInputLabel-root": { top: "-6px" } }}
              />
              <FormControl fullWidth>
                <InputLabel>Country</InputLabel>
                <Select
                  name="CountryId"
                  value={formData.CountryId}
                  onChange={handleChange}
                  required
                >
                  <MenuItem value="" disabled>Select Country</MenuItem>
                  {countries.map((country) => (
                    <MenuItem key={country.countryId} value={country.countryId}>
                      {country.countryName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>State</InputLabel>
                <Select
                  name="StateId"
                  value={formData.StateId}
                  onChange={handleChange}
                  required
                >
                  <MenuItem value="" disabled>Select State</MenuItem>
                  {states
                    .filter((state) => state.countryId == formData.CountryId)
                    .map((state) => (
                      <MenuItem key={state.stateId} value={state.stateId}>
                        {state.stateName}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>City</InputLabel>
                <Select
                  name="CityId"
                  value={formData.CityId}
                  onChange={handleChange}
                  required
                >
                  <MenuItem value="" disabled>Select City</MenuItem>
                  {cities
                    .filter((city) => city.stateId == formData.StateId)
                    .map((city) => (
                      <MenuItem key={city.cityId} value={city.cityId}>
                        {city.cityName}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              {/* <TextField
                label="Zip Code"
                name="ZipCode"
                value={formData.ZipCode}
                onChange={handleChange}
                fullWidth
                variant="outlined"
                sx={{ "& .MuiInputLabel-root": { top: "-6px" } }}
              /> */}
              <TextField
                label="Mobile No"
                name="MobileNo"
                value={formData.MobileNo}
                onChange={handleChange}
                fullWidth
                variant="outlined"
                sx={{ "& .MuiInputLabel-root": { top: "-6px" } }}
              />
              <TextField
                label="Phone No"
                name="PhoneNo"
                value={formData.PhoneNo}
                onChange={handleChange}
                fullWidth
                variant="outlined"
                sx={{ "& .MuiInputLabel-root": { top: "-6px" } }}
              />
              {/* <TextField
                label="TIN No"
                name="TinNo"
                value={formData.TinNo}
                onChange={handleChange}
                fullWidth
                variant="outlined"
                sx={{ "& .MuiInputLabel-root": { top: "-6px" } }}
              /> */}
              <TextField
                label="Affiliation No"
                name="AffiliationNo"
                value={formData.AffiliationNo}
                onChange={handleChange}
                fullWidth
                variant="outlined"
                sx={{ "& .MuiInputLabel-root": { top: "-6px" } }}
              />
              {/* <TextField
                label="Fax"
                name="Fax"
                value={formData.Fax}
                onChange={handleChange}
                fullWidth
                variant="outlined"
                sx={{ "& .MuiInputLabel-root": { top: "-6px" } }}
              /> */}
              <TextField
                label="Email ID"
                name="EmailID"
                value={formData.EmailID}
                onChange={handleChange}
                fullWidth
                type="email"
                variant="outlined"
                sx={{ "& .MuiInputLabel-root": { top: "-6px" } }}
              />
              {/* <TextField
                label="Website"
                name="Website"
                value={formData.Website}
                onChange={handleChange}
                fullWidth
                variant="outlined"
                sx={{ "& .MuiInputLabel-root": { top: "-6px" } }}
              /> */}
              <Box>
                <Typography>Branch Logo</Typography>
                {branchLogoPreview && (
                  <img
                    src={branchLogoPreview}
                    alt="Branch Logo"
                    style={{ maxWidth: "200px", marginTop: "8px" }}
                  />
                )}
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleOpenDialog}
                  style={{ marginTop: "8px" }}
                >
                  Upload Logo
                </Button>
                <input
                  type="file"
                  accept="image/jpeg,image/jpg"
                  onChange={handleFileChange}
                  ref={fileInputRef}
                  style={{ display: "none" }}
                />
                <Dialog open={openDialog} onClose={handleDialogClose}>
                  <DialogTitle>File Upload Instructions</DialogTitle>
                  <DialogContent>
                    <Typography variant="body1">
                      Please ensure the following when uploading a branch logo:
                    </Typography>
                    <ul>
                      <li>File size must be less than 2MB.</li>
                      <li>File format must be JPG or JPEG only.</li>
                    </ul>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={handleChooseFile} color="primary">
                      Choose File
                    </Button>
                    <Button onClick={handleDialogClose} color="secondary">
                      Cancel
                    </Button>
                  </DialogActions>
                </Dialog>
              </Box>
              <Box display="flex" gap={2} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button type="submit" size="large" variant="contained" color="primary">
                  Save
                </Button>
                <Button
                  variant="contained"
                  size="large"
                  color="error"
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          </form>
        )}
      </Paper>
      <Modal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        aria-labelledby="success-modal-title"
        aria-describedby="success-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ backgroundColor: '#0B1F40', padding: '12px 16px' }}>
            <Typography id="success-modal-title" variant="h6" sx={{ color: '#fff' }}>
              Success ✅
            </Typography>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <Typography id="success-modal-description" sx={{ mb: 2 }}>
              Branch added successfully
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate(-1);
                }}
                variant="contained"
                color="primary"
              >
                OK
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AddBranch;
